import React, { useRef, useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';

interface FlyoutPosition {
  left: number;
  maxHeight: number;
  top?: number;
  bottom?: number;
}

interface SidebarFlyoutProps {
  triggerEl: HTMLElement | null;
  direction: 'up' | 'down';
  isOpen: boolean;
  onClose: () => void;
  /** Largura mínima (default 240 — padrão dos menus da sidebar) */
  minWidth?: number;
  /** Largura fixa opcional (ex.: 256 no menu de conta) */
  width?: number;
  children: React.ReactNode;
  'data-testid'?: string;
}

/**
 * Shell dos flyouts da sidebar (portal manual — padrão dos menus de nav e
 * Configurações): position fixed, left = trigger.right + 22, direction 'up'
 * alinha por baixo / 'down' pelo topo, zIndex 9999, visual branco fixo.
 * `SidebarDropdown` (itens de nav) e o menu de conta usam este shell.
 */
export const SidebarFlyout: React.FC<SidebarFlyoutProps> = ({
  triggerEl,
  direction,
  isOpen,
  onClose,
  minWidth = 240,
  width,
  children,
  'data-testid': dataTestId,
}) => {
  const flyoutRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const [position, setPosition] = useState<FlyoutPosition | null>(null);

  // Calculate position imperatively when isOpen changes or window resizes
  const updatePosition = useCallback(() => {
    if (!triggerEl) {
      setPosition(null);
      return;
    }
    const rect = triggerEl.getBoundingClientRect();

    if (direction === 'up') {
      const bottom = window.innerHeight - rect.bottom - 2;
      setPosition({
        bottom,
        left: rect.right + 22,
        maxHeight: window.innerHeight - bottom - 16,
      });
    } else {
      setPosition({
        top: rect.top,
        left: rect.right + 22,
        maxHeight: window.innerHeight - rect.top - 16,
      });
    }
  }, [triggerEl, direction]);

  // Recalculate on open and on resize
  useEffect(() => {
    if (!isOpen) {
      setPosition(null);
      return;
    }
    updatePosition();

    window.addEventListener('resize', updatePosition);
    return () => window.removeEventListener('resize', updatePosition);
  }, [isOpen, updatePosition]);

  // Click-outside and ESC handlers
  useEffect(() => {
    if (!isOpen) return;

    const handleClick = (e: MouseEvent) => {
      if (
        flyoutRef.current && !flyoutRef.current.contains(e.target as Node) &&
        triggerEl && !triggerEl.contains(e.target as Node)
      ) {
        onClose();
      }
    };

    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose, triggerEl]);

  // Close on route change (skip initial mount)
  const prevPathname = useRef(location.pathname);
  useEffect(() => {
    if (prevPathname.current !== location.pathname) {
      onClose();
    }
    prevPathname.current = location.pathname;
  }, [location.pathname, onClose]);

  if (!isOpen || !position) return null;

  return createPortal(
    <div
      ref={flyoutRef}
      data-testid={dataTestId}
      style={{
        position: 'fixed',
        ...(direction === 'up'
          ? { bottom: position.bottom }
          : { top: position.top }),
        left: position.left,
        zIndex: 9999,
        minWidth: `${minWidth}px`,
        ...(width ? { width: `${width}px` } : {}),
        maxHeight: position.maxHeight,
        overflowY: 'auto' as const,
        backgroundColor: '#ffffff',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        padding: '6px',
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
        // Flyout é superfície light fixa (regra de dropdowns) — inocular vars temáticas
        // que o conteúdo possa usar, para não vazar a paleta dark sobre fundo branco.
        ...({ '--theme-error-color': '#ef4444' } as React.CSSProperties),
      }}
    >
      {children}
    </div>,
    document.body
  );
};
